import {
  arrayToEntityDict,
  type BuildPlatform,
  type LinkedPlayerBuilds,
  type LinkedPlayerBuildsListItem,
  type PlayerBuild
} from '@webapp2/shared/model'
import { postGetBody } from '@webapp2/shared/services/api'
import { computed } from 'vue'
import { isString, state } from '../state'
import { cache } from './cache'
import { isAdmin } from './permissions-service'

type PlatformBuild = { platform: BuildPlatform; build: PlayerBuild }

export function platformBuilds(link: LinkedPlayerBuilds): PlatformBuild[] {
  const builds: [BuildPlatform, PlayerBuild | undefined][] = [
    ['quest', link.questBuild],
    ['windows', link.windowsBuild],
    ['linux', link.linuxBuild]
  ]
  return builds.flatMap(([platform, build]) => (build ? [{ platform, build }] : []))
}

function playerBuildIds(link: LinkedPlayerBuilds): string[] {
  return platformBuilds(link).map(x => x.build.id)
}

function hasSamePlatforms(link: LinkedPlayerBuilds, platforms: BuildPlatform[]): boolean {
  const linkPlatforms = platformBuilds(link).map(x => x.platform)
  return linkPlatforms.length === platforms.length && platforms.every(x => linkPlatforms.includes(x))
}

export const linkedPlayerBuildsByPlayerBuildId = computed(() =>
  Object.values(state.linkedPlayerBuilds).reduce<{ [playerBuildId: string]: LinkedPlayerBuilds }>((acc, link) => {
    playerBuildIds(link).forEach(id => {
      acc[id] = link
    })
    return acc
  }, {})
)

async function loadLinks(type: 'LinkedPlayerBuilds' | 'LinkedPlayerBuildsByPlayerBuildId', idsToLoad: string[]) {
  if (idsToLoad.length === 0) {
    return
  }
  const links = await postGetBody<LinkedPlayerBuilds[]>('documents-by-id', { type, ids: idsToLoad })
  if (!links) {
    return
  }
  Object.assign(state.linkedPlayerBuilds, arrayToEntityDict(links))
  await cache.setItems(links)
}

export async function ensureLinkedPlayerBuilds(ids: string[]) {
  const idsToLoad = [...new Set(ids.filter(isString))].filter(id => !(id in state.linkedPlayerBuilds))
  await loadLinks('LinkedPlayerBuilds', idsToLoad)
}

export async function ensureLinkedPlayerBuildsByPlayerBuildId(playerBuildIds: string[]) {
  const idsToLoad = [...new Set(playerBuildIds.filter(isString))].filter(
    id => !(id in linkedPlayerBuildsByPlayerBuildId.value)
  )
  await loadLinks('LinkedPlayerBuildsByPlayerBuildId', idsToLoad)
}

export function invalidateLinkedPlayerBuildsWhosePlatformsChanged(listItems: LinkedPlayerBuildsListItem[]) {
  const changedIds = listItems
    .filter(x => {
      const link = state.linkedPlayerBuilds[x.id]
      return link && !hasSamePlatforms(link, x.platforms)
    })
    .map(x => x.id)
  changedIds.forEach(id => delete state.linkedPlayerBuilds[id])
  void cache.deleteItems(changedIds)
}

export function findEmbeddedModule(linkedPlayerBuildsId: string, techniqueId: string) {
  return state.linkedPlayerBuilds[linkedPlayerBuildsId]?.embeddedModules.find(
    x => x.techniqueId.toLowerCase() === techniqueId.toLowerCase()
  )
}

export const currentLinkedPlayerBuilds = computed(() => {
  const linkedPlayerBuildsId = state.vuplex?.linkedPlayerBuildsId
  if (linkedPlayerBuildsId) {
    return state.linkedPlayerBuilds[linkedPlayerBuildsId]
  }
  const playerBuildId = state.vuplex?.playerBuildId
  return playerBuildId ? linkedPlayerBuildsByPlayerBuildId.value[playerBuildId] : undefined
})

export async function ensureCurrentLinkedPlayerBuilds() {
  const linkedPlayerBuildsId = state.vuplex?.linkedPlayerBuildsId
  if (linkedPlayerBuildsId) {
    await ensureLinkedPlayerBuilds([linkedPlayerBuildsId])
    return
  }
  const playerBuildId = state.vuplex?.playerBuildId
  if (playerBuildId) {
    await ensureLinkedPlayerBuildsByPlayerBuildId([playerBuildId])
  }
}

export const currentPlayerBuildMetaRelease = computed(() => {
  const installedBuild = currentLinkedPlayerBuilds.value
  const latestSubmittedBuild = state.latestPlayerBuildSubmittedToMeta
  if (!isAdmin.value || !installedBuild || !latestSubmittedBuild) {
    return undefined
  }
  return {
    installedBuildId: installedBuild.id,
    latestSubmittedBuildId: latestSubmittedBuild.id,
    isLatest: installedBuild.id === latestSubmittedBuild.id
  }
})
